import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  searchQuestionNextPage,
  searchProfileNextPage,
  searchOpinionWithTerm,
} from 'app/dispatcher/searchDispatcher';
import { SEARCH_TYPE } from 'app/constants/search';
import { EmptySearchList } from 'app/components/blog/search';
import OpinionList from 'app/components/opinion/OpinionList';
import ProfileList from 'app/components/profile/ProfileList';
import JoinGroupList from 'app/components/group/JoinGroupList';
import ProductSearchList from 'app/components/product/ProductSearchList';
import useSearchArticles from 'entity/blog/hooks/useSearchArticles';
import scrollTo from 'app/utils/scrollTo';
import ArticleList from 'app/components/blog/ArticleList';
import SearchListWrapper from './SearchListWrapper';
import PostList from '../../components/post/PostList';
import AllResults from './AllResults';

const ListBySearchType = props => {
  const {
    productCategoryResult,
    groupResult,
    searchTerm,
    selectedFilter,
    setSelectedFilter,
  } = props;

  const countBySearchType = useSelector(state => {
    return {
      [SEARCH_TYPE.QUESTION]: state.post.pagination.totalCount,
      [SEARCH_TYPE.PROFILE]: state.profile.meta.pagination.totalCount,
      [SEARCH_TYPE.PRODUCT]: state.product.meta.pagination.totalCount,
      [SEARCH_TYPE.OPINION]: state.opinion.pagination.totalCount,
    };
  }, shallowEqual);
  const profileIds = useSelector(state => state.profile.meta.ids, shallowEqual);
  const profilePagination = useSelector(
    state => state.profile.meta.pagination,
    shallowEqual,
  );
  const questionPagination = useSelector(
    state => state.post.pagination,
    shallowEqual,
  );
  const opinionPagination = useSelector(state => state.opinion.pagination);

  const dispatch = useDispatch();

  const handleLoadMorePost = () => {
    dispatch(
      searchQuestionNextPage({
        term: searchTerm,
        pagination: questionPagination,
      }),
    );
  };

  const handleLoadMoreProfile = () => {
    dispatch(
      searchProfileNextPage({
        term: searchTerm,
        pagination: profilePagination,
      }),
    );
  };

  // handle article search
  const {
    result,
    isLoading,
    pagination,
    getSearchArticleByPage,
  } = useSearchArticles(searchTerm);

  switch (selectedFilter) {
    case SEARCH_TYPE.ALL:
      return (
        <AllResults
          searchTerm={searchTerm}
          setSelectedFilter={setSelectedFilter}
          productCategoryResult={productCategoryResult}
          groupResult={groupResult}
          articleResult={result}
          articlePagination={pagination}
        />
      );
    case SEARCH_TYPE.QUESTION:
      return (
        <SearchListWrapper
          count={countBySearchType[SEARCH_TYPE.QUESTION]}
          type="Discussions"
          term={searchTerm}
        >
          <PostList handleLoadMorePost={handleLoadMorePost} />
        </SearchListWrapper>
      );
    case SEARCH_TYPE.PROFILE:
      return (
        <SearchListWrapper
          count={countBySearchType[SEARCH_TYPE.PROFILE]}
          type="Profiles"
          term={searchTerm}
        >
          <ProfileList
            profileIds={profileIds}
            handleLoadMoreProfile={handleLoadMoreProfile}
          />
        </SearchListWrapper>
      );
    case SEARCH_TYPE.PRODUCT:
      return (
        <SearchListWrapper
          count={countBySearchType[SEARCH_TYPE.PRODUCT]}
          type="Products"
          term={searchTerm}
        >
          <ProductSearchList
            searchTerm={searchTerm}
            onPageChange={() => scrollTo({ top: 0 })}
          />
        </SearchListWrapper>
      );
    case SEARCH_TYPE.ARTICLE:
      return (
        <SearchListWrapper
          count={pagination.total}
          type="Articles"
          term={searchTerm}
        >
          <ArticleList
            articles={result}
            loading={isLoading}
            currentPage={pagination.page}
            totalPage={pagination.totalPages}
            onPageChange={({ selected }) => {
              getSearchArticleByPage(selected + 1);
            }}
            renderEmptyList={() => <EmptySearchList />}
          />
        </SearchListWrapper>
      );
    case SEARCH_TYPE.OPINION:
      return (
        <SearchListWrapper
          type="Opinions"
          count={countBySearchType[SEARCH_TYPE.OPINION]}
          term={searchTerm}
        >
          <OpinionList
            handleLoadMoreOpinion={() => {
              const { currentPage: currentOpinionPage } = opinionPagination;
              const page = currentOpinionPage + 1;
              dispatch(
                searchOpinionWithTerm(
                  { term: searchTerm, perPage: 8, page },
                  { isFetchingMoreOpinions: true },
                ),
              );
            }}
            opinionItemProps={{ withComment: false }}
          />
        </SearchListWrapper>
      );
    case SEARCH_TYPE.GROUP:
      return (
        <SearchListWrapper
          type="Groups"
          count={groupResult.length}
          term={searchTerm}
        >
          <div className="bg-white p-4 h-fit w-full rounded-lg">
            <JoinGroupList groupList={groupResult} />
          </div>
        </SearchListWrapper>
      );
    default:
      return null;
  }
};

export default ListBySearchType;
